/**
 * EDSC-2772 â€” Vouched IDV support for OVS v1 (this app).
 *
 * Vouched has only ever run on the telegra-commerce OVS surface (affiliate
 * `ovsVersion: 'v2'`). For `ovsVersion: 'v1'` affiliates â€” the schema default â€”
 * patients land here instead, and `enabledEnhancedVerificationVouched` did
 * nothing at all: they silently kept getting the legacy "upload a photo of your
 * ID" question.
 *
 * The backend is already surface-agnostic, so nothing new is needed there.
 * Everything below is pure so it can be unit tested without a browser or the
 * Vouched CDN script; the React component and the API calls live alongside in
 * `pages/OnlineVisit/components/VouchedVerification` and
 * `shared/services/VouchedService`.
 */

export const IDENTIFICATION_QUESTIONNAIRE_ID = 'identification-questionnaire';

/** Vouched plugin bundle. Loaded from the CDN so no new npm dependency is added. */
export const VOUCHED_SCRIPT_URL =
  'https://static.vouched.id/plugin/releases/latest/index.js';

/** Container the plugin mounts into. Must exist in the DOM before mount(). */
export const VOUCHED_ELEMENT_ID = 'vouched-identity-element';

/** Poll interval so webhook / cross-device completion is picked up. */
export const VERIFICATION_STATUS_POLL_INTERVAL_MS = 10_000;

/** Max wait for `window.Vouched` after the script tag is present. */
export const VOUCHED_SCRIPT_LOAD_TIMEOUT_MS = 30_000;

/**
 * Statuses returned by GET /v2/patients/vouchedVerificationStatus and
 * POST /v2/patients/vouchedVerification/start.
 */
export type TVouchedApiStatus =
  | 'verification_completed'
  | 'verification_configuration_error'
  | 'verification_idv_required'
  | 'verification_job_completed_successfully'
  | 'verification_job_expired'
  | 'verification_job_failed'
  | 'verification_job_in_progress'
  | 'verification_job_not_started'
  | 'verification_job_removed'
  | 'vouched_api_request_failed';

export type TVouchedVerificationError = {
  type?: string;
  message?: string;
};

export type TVouchedStatusResponse = {
  status?: TVouchedApiStatus | string | null;
  vouchedPublicKey?: string | null;
  /** Present only when the backend is willing to expose the IDV plugin. */
  idvPluginVerification?: Record<string, unknown> | null;
  vouchedJobId?: string | null;
  vouchedIdentityVerified?: boolean | null;
  /** Step-up stage on a POST /start response ('crosscheck' | 'dob' | 'idv'). */
  stage?: string | null;
  vouchedVerificationToken?: string | null;
  vouchedVerificationTokenExpiryDate?: string | Date | null;
  vouchedVerificationErrors?: TVouchedVerificationError[] | null;
  email?: string | null;
  phone?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  dateOfBirth?: string | null;
};

/**
 * What the OVS v1 identification step should do next.
 *
 * `unavailable` is the important one: it is the answer for every affiliate that
 * does not have Vouched enabled, and it means "render exactly what this app
 * rendered before this change".
 */
export type TVouchedFlowState =
  | 'unavailable'
  | 'configuration_error'
  | 'verified'
  | 'in_progress'
  | 'idv_required'
  /** Terminal job failure — show errors + start-new / retry (commerce error_start_new). */
  | 'verification_failed'
  /** Transient API failure — show retry (commerce api_error_retry). */
  | 'api_error_retry';

/** Statuses where the patient must see failure UI before starting again. */
export const isTerminalVerificationFailureStatus = (
  status?: TVouchedApiStatus | string | null
): boolean =>
  status === 'verification_job_failed' ||
  status === 'verification_job_removed' ||
  status === 'verification_job_expired';

/** Human-readable error type label (matches commerce IdentityVerificationStep). */
export const formatVouchedErrorTitle = (type?: string | null): string => {
  if (!type) return 'Error';
  return type.replace(/([a-z])([A-Z])/g, '$1 $2').trim();
};

/** Non-empty vouchedVerificationErrors from a status payload. */
export const getVouchedStatusErrors = (
  status?: TVouchedStatusResponse | null
): TVouchedVerificationError[] => {
  const errors = status?.vouchedVerificationErrors;
  return Array.isArray(errors) ? errors.filter(Boolean) : [];
};

/** Button label for starting a fresh verification session (commerce-aligned). */
export const getStartNewVerificationLabel = (
  status?: TVouchedApiStatus | string | null
): 'Retry verification' | 'Start new verification' =>
  status === 'verification_job_failed'
    ? 'Retry verification'
    : 'Start new verification';

export const isIdentificationQuestionnaire = (
  questionnaireId?: string | null
): boolean => questionnaireId === IDENTIFICATION_QUESTIONNAIRE_ID;

/**
 * Capability detection, straight off the status / start response.
 *
 * Backend only emits `vouchedPublicKey` + `idvPluginVerification` when step-up
 * is enabled AND stage is already IDV (or start just returned
 * `verification_idv_required`). A bare GET of `verification_job_not_started`
 * deliberately has no key â€” that does NOT mean Vouched is off. Callers that
 * decide whether to POST /start must not use this alone as a hard "unavailable"
 * gate (see VouchedVerification begin()).
 */
export const isVouchedIdvAvailable = (
  status?: TVouchedStatusResponse | null
): boolean =>
  Boolean(status?.vouchedPublicKey && status?.idvPluginVerification);

/**
 * EDSC-2875 - a completion that came out of the step-up run itself.
 *
 * The backend has two emitters of `verification_completed`:
 * `completeStepUpVerification`'s own return and `buildCompletedResponse`. Both
 * are reachable only through `POST /v2/patients/vouchedVerification/start` -
 * every call site that surfaces the value to a route sits under it, and the one
 * caller on an ungated path, `syncVouchedVerificationLog`, discards its
 * completion result. `POST /start` refuses the call outright when the affiliate
 * does not have Vouched enabled (`startStepUpVerificationService` throws ahead
 * of both the reuse path and `runStepUpVerification`, which throws again), so
 * receiving this status is itself proof that step-up is on for this affiliate.
 *
 * It also carries no `vouchedPublicKey`, and correctly so: a Crosscheck or DOB
 * pass concludes server-side and the IDV plugin never runs, so there are no
 * plugin credentials to hand out. Without this predicate that key-less
 * completion is indistinguishable from the kill-switch case in
 * VouchedVerification applyStatus, and a patient who has already been verified
 * is dropped back onto the legacy upload question.
 *
 * The GET status route never returns this status - a live-job completion there
 * is `verification_job_completed_successfully`, which is NOT affiliate-gated
 * and so must keep going through the ownership check.
 *
 * `vouchedIdentityVerified` is checked too. The backend never emits a completion
 * without it, so this is belt-and-braces: it keeps the predicate from widening
 * the guard on a payload that says completed but not verified.
 */
export const isStepUpCompletionStatus = (
  status?: TVouchedStatusResponse | null
): boolean =>
  status?.status === 'verification_completed' &&
  status?.vouchedIdentityVerified === true;

const VOUCHED_PATIENT_PREFILL_KEYS = [
  'firstName',
  'lastName',
  'phone',
  'email',
  'dateOfBirth'
] as const;

export type TVouchedPatientPrefill = Pick<
  TVouchedStatusResponse,
  typeof VOUCHED_PATIENT_PREFILL_KEYS[number]
>;

const hasVouchedPatientPrefillValue = (
  value: string | null | undefined
): value is string => value != null && String(value).trim() !== '';

/**
 * After the patient edits identity, overlay those fields onto a status payload
 * so the IDV plugin is not stuck with a stale GET / thin POST start response.
 * Empty override values are ignored. Session/status flags are left untouched.
 * When overrides are null/undefined this is a no-op (same reference returned).
 */
export const applyVouchedPatientPrefillOverrides = (
  status: TVouchedStatusResponse,
  overrides?: TVouchedPatientPrefill | null
): TVouchedStatusResponse => {
  if (!overrides) return status;

  const next: TVouchedStatusResponse = { ...status };
  for (const key of VOUCHED_PATIENT_PREFILL_KEYS) {
    if (hasVouchedPatientPrefillValue(overrides[key])) {
      next[key] = overrides[key];
    }
  }
  return next;
};

/**
 */
export const mergeVouchedPatientPrefill = (
  current: TVouchedStatusResponse | null | undefined,
  started: TVouchedStatusResponse
): TVouchedStatusResponse => {
  const merged: TVouchedStatusResponse = { ...started };

  for (const key of VOUCHED_PATIENT_PREFILL_KEYS) {
    if (hasVouchedPatientPrefillValue(started[key])) {
      continue;
    }
    const fromGet = current?.[key];
    if (hasVouchedPatientPrefillValue(fromGet)) {
      merged[key] = fromGet;
    }
  }

  return merged;
};

/**
 * Treat a token as usable only if it exists AND has not expired - a stale token
 * makes the plugin resume a session Vouched has already closed.
 */
export const isVouchedTokenValid = (
  token?: string | null,
  expiry?: string | Date | null,
  now: number = Date.now()
): boolean => {
  if (!token || !expiry) return false;
  const expiryMs =
    expiry instanceof Date ? expiry.getTime() : new Date(expiry).getTime();
  if (Number.isNaN(expiryMs)) return false;
  return now < expiryMs;
};

/**
 * Whether the initial GET status is far enough along that POST /start must not
 * run again. Mirrors commerce IdentityVerificationFlow:
 * - `verification_job_not_started` always needs start (even when a public key is
 *   already exposed for stage=idv)
 * - `verification_job_in_progress` with a valid token is a resume
 * - `verification_idv_required` means step-up already finished
 */
export const shouldSkipStepUpStart = (
  status?: TVouchedStatusResponse | null
): boolean => {
  if (!status) return false;

  if (
    status.vouchedIdentityVerified === true ||
    status.status === 'verification_job_completed_successfully' ||
    status.status === 'verification_completed' ||
    status.status === 'verification_configuration_error'
  ) {
    return true;
  }

  if (
    status.status === 'verification_job_in_progress' &&
    isVouchedIdvAvailable(status) &&
    isVouchedTokenValid(
      status.vouchedVerificationToken,
      status.vouchedVerificationTokenExpiryDate
    )
  ) {
    return true;
  }

  if (
    status.status === 'verification_idv_required' &&
    isVouchedIdvAvailable(status)
  ) {
    return true;
  }

  // Show failure UI first; patient starts a new session explicitly (commerce-aligned).
  if (isTerminalVerificationFailureStatus(status.status)) {
    return true;
  }

  if (status.status === 'vouched_api_request_failed') {
    return true;
  }

  return false;
};

export const resolveVouchedFlowState = (
  status?: TVouchedStatusResponse | null
): TVouchedFlowState => {
  if (!status) return 'unavailable';

  // A configuration error is reported even without a public key (that is the
  // whole point of it), so it has to be checked before capability.
  if (status.status === 'verification_configuration_error') {
    return 'configuration_error';
  }

  if (
    status.vouchedIdentityVerified === true ||
    status.status === 'verification_job_completed_successfully' ||
    status.status === 'verification_completed'
  ) {
    return 'verified';
  }

  // No plugin credentials yet. Pre-step-up `verification_job_not_started` looks
  // the same as "affiliate does not have Vouched" from GET alone — callers that
  // still need to run start must handle that before applying this result as
  // terminal unavailable (VouchedVerification begin()).
  if (!isVouchedIdvAvailable(status)) return 'unavailable';

  if (status.status === 'verification_job_in_progress') return 'in_progress';

  if (status.status === 'vouched_api_request_failed') {
    return 'api_error_retry';
  }

  if (isTerminalVerificationFailureStatus(status.status)) {
    return 'verification_failed';
  }

  // job_not_started (with key after stage=idv) → mount the widget.
  return 'idv_required';
};

/**
 * postCheckout webhook URL. OVS v1 always starts from
 * `/startVisit?orderId=…`, so an order and instance are always in hand and the
 * preCheckout (journey) variant used by commerce does not apply here.
 *
 * Returns undefined when any part is missing rather than building a URL with
 * "undefined" in the path â€” without a callback the widget still works, it just
 * relies on polling, whereas a malformed callback silently black-holes results.
 */
export const buildPostCheckoutCallbackUrl = ({
  apiBaseUrl,
  orderId,
  questionnaireId,
  questionnaireInstanceId
}: {
  apiBaseUrl?: string | null;
  orderId?: string | null;
  questionnaireId?: string | null;
  questionnaireInstanceId?: string | null;
}): string | undefined => {
  if (!apiBaseUrl || !orderId || !questionnaireId || !questionnaireInstanceId) {
    return undefined;
  }
  const base = apiBaseUrl.replace(/\/+$/, '');
  return `${base}/v2/vouchedWebhook/postCheckout/${encodeURIComponent(
    orderId
  )}/${encodeURIComponent(questionnaireId)}/${encodeURIComponent(
    questionnaireInstanceId
  )}`;
};

/** The Vouched IDV plugin accepts MM/DD/YYYY. */
export const formatBirthDateForVouched = (
  dob?: string | null
): string | undefined => {
  if (!dob) return undefined;
  const trimmed = String(dob).trim();
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(trimmed)) return trimmed;
  const isoMatch = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) {
    const [, year, month, day] = isoMatch;
    return `${month}/${day}/${year}`;
  }
  return undefined;
};

/** Ten-digit US phone, matching the backend's Crosscheck/DOB normalization. */
export const formatPhoneForVouched = (
  phone?: string | null
): string | undefined => {
  if (!phone) return undefined;
  const digits = String(phone).replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('1')) return digits.slice(1);
  if (digits.length === 10) return digits;
  return undefined;
};

export type TVouchedPluginConfig = {
  appId: string;
  publicKey: string;
  type: 'idv';
  face: 'both';
  id: 'both';
  includeBackId: boolean;
  liveness: 'enhanced';
  crossDevice: boolean;
  crossDeviceQRCode: boolean;
  crossDeviceSMS: boolean;
  callbackURL?: string;
  token?: string;
  userConfirmation?: {
    confirmData: boolean;
    confirmImages: boolean;
  };
  verification: {
    email?: string;
    phone?: string;
    firstName?: string;
    lastName?: string;
    birthDate?: string;
    /**
     * Stage 1 Crosscheck already ran server-side. Leaving the plugin's embedded
     * crosscheck on would re-run and re-bill it (IC-5774 AC: "IDV plugin mounts
     * with crosscheck: false").
     */
    enableCrossCheck: false;
  };
};

/**
 * Build the plugin config. Callbacks (onInit/onDone/onError) are deliberately
 * not built here so this stays pure and testable â€” the component spreads them in.
 */
export const buildVouchedConfig = ({
  appId,
  callbackUrl,
  token,
  prefill
}: {
  appId: string;
  callbackUrl?: string;
  token?: string;
  prefill?: {
    email?: string | null;
    phone?: string | null;
    firstName?: string | null;
    lastName?: string | null;
    dateOfBirth?: string | null;
  } | null;
}): TVouchedPluginConfig => {
  const phone = formatPhoneForVouched(prefill?.phone);
  const birthDate = formatBirthDateForVouched(prefill?.dateOfBirth);

  return {
    appId,
    publicKey: appId,
    type: 'idv',
    face: 'both',
    id: 'both',
    includeBackId: true,
    liveness: 'enhanced',
    crossDevice: true,
    crossDeviceQRCode: true,
    crossDeviceSMS: true,
    ...(callbackUrl ? { callbackURL: callbackUrl } : {}),
    ...(token ? { token } : {}),
    userConfirmation: {
      confirmData: true,
      confirmImages: true
    },
    verification: {
      ...(prefill?.email ? { email: prefill.email } : {}),
      ...(phone ? { phone } : {}),
      ...(prefill?.firstName ? { firstName: prefill.firstName } : {}),
      ...(prefill?.lastName ? { lastName: prefill.lastName } : {}),
      ...(birthDate ? { birthDate } : {}),
      enableCrossCheck: false
    }
  };
};

/* ------------------------------------------------------------------------- *
 * Failsafe ownership registry
 * ------------------------------------------------------------------------- */

const VOUCHED_OWNED_QI_PREFIX = 'vouched_owned_qi:';

/**
 * sessionStorage rather than redux state on purpose: the Vouched flow can hop to
 * a phone and back (crossDevice/QR/SMS) and the patient can reload mid-capture,
 * either of which wipes in-memory state â€” and the failsafe below must stay
 * suppressed across both.
 */
const sessionStore = (): Storage | null => {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : null;
  } catch {
    // Safari private mode throws on access.
    return null;
  }
};

export const markIdentificationOwnedByVouched = (
  questionnaireInstanceId?: string | null
): void => {
  if (!questionnaireInstanceId) return;
  try {
    sessionStore()?.setItem(
      `${VOUCHED_OWNED_QI_PREFIX}${questionnaireInstanceId}`,
      'true'
    );
  } catch {
    // Storage full / blocked â€” the failsafe stays enabled, which is the old
    // behaviour rather than a new failure mode.
  }
};

export const isIdentificationOwnedByVouched = (
  questionnaireInstanceId?: string | null
): boolean => {
  if (!questionnaireInstanceId) return false;
  try {
    return (
      sessionStore()?.getItem(
        `${VOUCHED_OWNED_QI_PREFIX}${questionnaireInstanceId}`
      ) === 'true'
    );
  } catch {
    return false;
  }
};

export const clearIdentificationOwnedByVouched = (
  questionnaireInstanceId?: string | null
): void => {
  if (!questionnaireInstanceId) return;
  try {
    sessionStore()?.removeItem(
      `${VOUCHED_OWNED_QI_PREFIX}${questionnaireInstanceId}`
    );
  } catch {
    // no-op
  }
};

/**
 * Gate for the "BRUTE FORCE FAILSAFE" in store/onlineVisit/saga.ts, which
 * force-completes the identification questionnaire on ANY error and tells the
 * patient "Your identification has been received."
 *
 * That is correct for a file upload (a failed upload should not dead-end an
 * order) and catastrophic for Vouched: it would mark identity valid without a
 * verification ever passing â€” the same defect class as EDSC-2760, where
 * Crosscheck auto-passed every verification.
 *
 * Returns true when the legacy force-complete may run.
 */
export const shouldForceCompleteIdentification = ({
  questionnaireId,
  questionnaireInstanceId,
  answerDataInstanceId
}: {
  questionnaireId?: string | null;
  questionnaireInstanceId?: string | null;
  answerDataInstanceId?: string | null;
}): boolean => {
  const looksLikeIdentification =
    isIdentificationQuestionnaire(questionnaireId) ||
    (typeof answerDataInstanceId === 'string' &&
      answerDataInstanceId.includes('identification'));

  if (!looksLikeIdentification) return false;

  /**
   * Check BOTH identifiers, not just the preferred one. The saga passes
   * `currentQI?.id` and `answerData.questionnaireInstance` independently, and if
   * they disagree, looking at only one would miss the marker and force-complete
   * an instance Vouched owns â€” precisely what this guard exists to prevent. Any
   * match means hands off.
   */
  return !(
    isIdentificationOwnedByVouched(questionnaireInstanceId) ||
    isIdentificationOwnedByVouched(answerDataInstanceId)
  );
};
